// Commissions — admin-level roles get the management tabs; everyone else gets My
// Commissions (their own entries only; backend 403s enforce it either way).
//
// Pay numbers on this page are printed exactly as the backend computed them —
// nothing here adds, subtracts or re-derives a commission figure. Money keeps its
// cents. A status with no entries, and a HELD plan (stored at 0 because its basis
// is undecided), print "—", never "$0".
import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import apiClient from '../../api/client';
import { getUserRole } from '../../helpers/tenantHelpers';
import Button from '../../components/ui/button';
import Badge from '../../components/ui/badge';
import { DataTable, Table, THead, TRow, TCell, TEmpty } from '../../components/ui/table';
import { TabBar } from '../../components/ui/tabs';
import RevivalHeader, { StatStrip } from '../../components/Revival/RevivalHeader';
import { count, money, rate, sentence, shortDate, words } from './salesFormat';
import '../../components/Revival/revival.css';
import './sales-ds.css';

const STATUS_TONE = {
  projected: 'neutral',
  held_for_review: 'warning',
  pending: 'warning',
  earned: 'info',
  payable: 'info',
  paid: 'success',
  charged_back: 'danger',
  open: 'warning',
  reviewed: 'info',
};

const TRIGGER_LABELS = { signed: 'Quote Signed', first_service_complete: 'First Service', payment_collected: 'Payment Collected' };
const STRUCTURE_LABELS = { pct_contract: '% of Year-1 Contract', pct_initial_service: '% of Initial Service', flat: 'Flat Amount' };
const CHARGEBACK_LABELS = { none: 'No Chargeback', full_within_days: 'Full Chargeback', prorated_within_days: 'Pro-rated' };
const SOURCE_LABELS = { fr_direct: 'FieldRoutes', quote_linked: 'Quote' };
const STATUS_ORDER = ['', 'projected', 'held_for_review', 'pending', 'earned', 'payable', 'paid', 'charged_back'];

// Available actions per status: [targetStatus, label, buttonVariant].
// Mirrors the backend transition graph exactly (mark paid AND unmark). Row actions
// are plain buttons: a table of accent fills is not one primary per screen.
const ACTIONS = {
  projected: [['pending', 'Approve', 'secondary']],
  held_for_review: [['pending', 'Approve', 'secondary'], ['projected', 'Un-hold', 'ghost']],
  pending: [['earned', 'Mark Earned', 'secondary'], ['projected', 'Revert', 'ghost']],
  earned: [['payable', 'Mark Payable', 'secondary'], ['pending', 'Unmark', 'ghost'], ['charged_back', 'Chargeback', 'danger']],
  payable: [['paid', 'Mark Paid', 'secondary'], ['earned', 'Unmark', 'ghost'], ['charged_back', 'Chargeback', 'danger']],
  paid: [['payable', 'Unmark Paid', 'ghost'], ['charged_back', 'Chargeback', 'danger']],
  charged_back: [['earned', 'Reverse CB', 'ghost']],
};

const StatusBadge = ({ status }) => <Badge tone={STATUS_TONE[status] || 'neutral'}>{sentence(status)}</Badge>;
const Loading = () => <div className="rv-loading">Loading…</div>;

// Consequence hint — hover (title) or tap reveals what this does to a rep's
// check, not a definition.
function Hint({ text }) {
  const [open, setOpen] = useState(false);
  return (
    <span className="sl-hint">
      <button type="button" title={text} aria-label="What this means"
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen(o => !o); }}
        onBlur={() => setOpen(false)}>
        ?
      </button>
      {open ? <span className="sl-hint-pop" role="tooltip">{text}</span> : null}
    </span>
  );
}

const HINTS = {
  tier_mode_bracket: "Each slice of the month pays its own rate: e.g. the first $15,000 of completed service value pays 20%, everything above pays 30%. Checks already cut never change.",
  tier_mode_whole: "Once a rep passes a threshold, their ENTIRE month pays the new rate — including services completed before they crossed. Nothing in the month is payable until month-end.",
  threshold: "When a rep's completed service value this month reaches this number, this rate kicks in. Basis is service value completed — not commission dollars, not appointment count.",
  bonus: "One-time add: hit this much completed service value in a month and this amount lands on the rep's check once. Off when blank.",
};

// WHY each bucket exists + what moves someone out of it.
const BUCKET_HINTS = {
  eligible: "Mapped to an active commission profile — completed services accrue under the tier ladder.",
  excluded_no_profile: "This seller has no active commission profile: their sales are counted but never accrue. Assign a commission-type compensation profile to move them to eligible.",
  excluded_unmapped: "This FieldRoutes seller ID has no CRM user mapping, so sales can't be attributed. Set external_sales_rep_id on their CRM user to move them out.",
  held: "Billing shape (recurring/unpriced) makes the per-service basis undecided — this plan accrues nothing until the policy question is resolved. Never counted as $0.",
};

// ── Dashboard Tab ───────────────────────────────────────────────
const DASH_STATUSES = [
  ['projected', 'Projected'], ['held_for_review', 'Held'], ['pending', 'Pending'], ['earned', 'Earned'],
  ['payable', 'Payable'], ['paid', 'Paid'],
];

function DashboardTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let m = true;
    apiClient.get('/analytics/commissions/dashboard/').then(r => { if (m) setData(r.data); })
      .catch(() => toast.error('Failed to load commission dashboard'))
      .finally(() => { if (m) setLoading(false); });
    return () => { m = false; };
  }, []);

  if (loading) return <Loading />;
  if (!data) return <p className="sl-notice is-bad">The commission dashboard didn’t load.</p>;

  const bs = data.by_status || {};
  const src = data.by_source_path || {};
  const stat = ([key, label]) => {
    const row = bs[key];
    if (!row) return { label, value: '—', note: 'none' };
    // Held entries are stored at 0 because their basis is undecided: not a $0 total.
    if (key === 'held_for_review') return { label, value: '—', note: `${count(row.count)} held · not valued` };
    return { label, value: money(row.total), note: `${count(row.count)} ${row.count === 1 ? 'entry' : 'entries'}` };
  };
  const cb = bs.charged_back;
  return (
    <div className="sl-section">
      <StatStrip stats={DASH_STATUSES.map(stat)} />
      <p className="rv-note">
        Chargebacks {cb ? <>{money(cb.total)} · {count(cb.count)} {cb.count === 1 ? 'entry' : 'entries'}</> : '—'}
        {Object.keys(src).length ? <> · By source: {Object.entries(src).map(([k, v]) => `${SOURCE_LABELS[k] || words(k)} ${count(v.count)} (${money(v.total)})`).join(' · ')}</> : null}
      </p>
      <div className="sl-section-head"><h2>By rep</h2></div>
      <DataTable
        rowKey={(r) => r.rep_id ?? r.rep_name}
        columns={[
          { key: 'rep_name', label: 'Rep', render: (r) => r.rep_name || '—' },
          { key: 'total_earned', label: 'Earned', num: true, render: (r) => money(r.total_earned) },
          { key: 'total_chargebacks', label: 'Chargebacks', num: true, render: (r) => money(r.total_chargebacks) },
          { key: 'pending_count', label: 'Pending', num: true, render: (r) => count(r.pending_count) },
          { key: 'entry_count', label: 'Entries', num: true, render: (r) => count(r.entry_count) },
        ]}
        rows={data.by_rep || []}
        empty="No commission entries yet."
      />
    </div>
  );
}

// ── Entries Tab ─────────────────────────────────────────────────
function EntriesTab() {
  const [entries, setEntries] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [q, setQ] = useState('');
  const [qLive, setQLive] = useState('');

  // debounce search
  useEffect(() => { const t = setTimeout(() => setQ(qLive), 350); return () => clearTimeout(t); }, [qLive]);

  const load = useCallback(() => {
    setLoading(true);
    const params = {};
    if (statusFilter) params.status = statusFilter;
    if (sourceFilter) params.source_path = sourceFilter;
    if (from) params.from = from;
    if (to) params.to = to;
    if (q) params.q = q;
    apiClient.get('/analytics/commissions/entries/', { params })
      .then(r => { setEntries(r.data.results || []); setSummary(r.data.summary || null); })
      .catch(() => toast.error('Failed to load entries'))
      .finally(() => setLoading(false));
  }, [statusFilter, sourceFilter, from, to, q]);

  useEffect(() => { load(); }, [load]);

  const [openReps, setOpenReps] = useState({});
  const [bulkBusy, setBulkBusy] = useState(null);

  // Group by rep — one row each, expand for the entries. The group row counts
  // entries; it never sums money (the backend's filtered totals are above).
  const groups = React.useMemo(() => {
    const m = new Map();
    for (const e of entries) {
      const k = String(e.rep_id ?? 'none');
      if (!m.has(k)) m.set(k, { rep_id: k, rep_name: e.rep_name || 'Unattributed', rows: [], statuses: {} });
      const g = m.get(k);
      g.rows.push(e);
      g.statuses[e.status] = (g.statuses[e.status] || 0) + 1;
    }
    return [...m.values()].sort((a, b) => a.rep_name.localeCompare(b.rep_name));
  }, [entries]);

  const bulkApprove = async (g) => {
    const targets = g.rows.filter(e => e.status === 'projected' || e.status === 'held_for_review');
    if (!targets.length) { toast.info('Nothing to approve for this rep'); return; }
    if (!window.confirm(`Approve ${targets.length} entr${targets.length === 1 ? 'y' : 'ies'} for ${g.rep_name}? They move to pending (still not payable).`)) return;
    setBulkBusy(g.rep_id);
    let ok = 0, fail = 0;
    for (const e of targets) {
      try { await apiClient.post(`/analytics/commissions/entries/${e.id}/transition/`, { status: 'pending' }); ok++; }
      catch { fail++; }
    }
    (fail ? toast.warn : toast.success)(`Approved ${ok}${fail ? `, ${fail} failed` : ''} for ${g.rep_name}`);
    setBulkBusy(null);
    load();
  };

  const doTransition = async (entry, target, label) => {
    let body = { status: target };
    if (target === 'charged_back') {
      const input = window.prompt(`Chargeback amount for ${entry.rep_name || 'rep'} (blank = full ${money(entry.commission_amount)}):`, '');
      if (input === null) return; // cancelled
      if (input.trim() !== '') body.chargeback_amount = input.trim();
    }
    setBusy(entry.id);
    try {
      await apiClient.post(`/analytics/commissions/entries/${entry.id}/transition/`, body);
      toast.success(label + ' ✓');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || 'Transition failed');
    } finally { setBusy(null); }
  };

  const all = summary?._all;
  const clearFilters = () => { setStatusFilter(''); setSourceFilter(''); setFrom(''); setTo(''); setQLive(''); setQ(''); };
  const filtered = !!(statusFilter || sourceFilter || from || to || qLive);

  return (
    <div className="sl-section">
      {/* Summary — the backend's totals for exactly what's filtered */}
      {all ? (
        <StatStrip stats={[
          { label: filtered ? 'Entries (filtered)' : 'Entries', value: count(all.count) },
          { label: 'Gross commission', value: money(all.gross) },
          { label: 'Chargebacks', value: money(all.chargebacks) },
          { label: 'Net', value: money(all.net) },
        ]} />
      ) : null}

      <div className="rv-chips" role="toolbar" aria-label="Filter by status">
        {STATUS_ORDER.map(s => (
          <button key={s || 'all'} type="button" className={`rv-chip${statusFilter === s ? ' is-active' : ''}`} aria-pressed={statusFilter === s} onClick={() => setStatusFilter(s)}>
            {s ? sentence(s) : 'All'}
          </button>
        ))}
      </div>
      <div className="sl-controls">
        <select value={sourceFilter} onChange={e => setSourceFilter(e.target.value)} aria-label="Source">
          <option value="">All sources</option>
          <option value="fr_direct">FieldRoutes</option>
          <option value="quote_linked">Quote</option>
        </select>
        <input type="date" value={from} onChange={e => setFrom(e.target.value)} title="Accrued from" aria-label="Accrued from" />
        <input type="date" value={to} onChange={e => setTo(e.target.value)} title="Accrued to" aria-label="Accrued to" />
        <input placeholder="Search rep, customer or plan" value={qLive} onChange={e => setQLive(e.target.value)} aria-label="Search" />
        {filtered ? <Button variant="ghost" size="sm" onClick={clearFilters}>Clear</Button> : null}
      </div>

      {loading ? <Loading /> : (
        <div>
          {groups.map(g => {
            const open = !!openReps[g.rep_id];
            const approvable = g.rows.filter(e => e.status === 'projected' || e.status === 'held_for_review').length;
            return (
              <div key={g.rep_id} className="sl-group">
                <div className="sl-group-row" role="button" tabIndex={0} aria-expanded={open}
                  onClick={() => setOpenReps(o => ({ ...o, [g.rep_id]: !o[g.rep_id] }))}
                  onKeyDown={(ev) => { if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); setOpenReps(o => ({ ...o, [g.rep_id]: !o[g.rep_id] })); } }}>
                  <span className="sl-caret" aria-hidden="true">{open ? '▾' : '▸'}</span>
                  <b>{g.rep_name}</b>
                  <span className="sl-muted">{g.rows.length} entr{g.rows.length === 1 ? 'y' : 'ies'}</span>
                  <span className="sl-actions">
                    {Object.entries(g.statuses).map(([s, n]) => <Badge key={s} tone={STATUS_TONE[s] || 'neutral'}>{n} {words(s)}</Badge>)}
                  </span>
                  <span className="sl-end">
                    {approvable > 0 ? (
                      <Button size="sm" variant="secondary" loading={bulkBusy === g.rep_id}
                        onClick={(ev) => { ev.stopPropagation(); bulkApprove(g); }}>
                        Approve all ({approvable})
                      </Button>
                    ) : null}
                  </span>
                </div>
                {open ? (
                  <div className="sl-group-body">
                    <Table>
                      <THead columns={['Status', 'Source', 'Customer', { label: 'Contract', num: true }, { label: 'Commission', num: true }, 'Accrued', '']} />
                      <tbody>
                        {g.rows.map(e => (
                          <TRow key={e.id}>
                            <TCell><StatusBadge status={e.status} /></TCell>
                            <TCell className="sl-muted">{SOURCE_LABELS[e.source_path] || words(e.source_path)}{e.fr_plan_external_id ? ` · ${e.fr_plan_external_id}` : ''}</TCell>
                            <TCell>{e.customer_name || '—'}</TCell>
                            <TCell num>{money(e.contract_value)}</TCell>
                            <TCell num>
                              <span className="sl-strong">{money(e.commission_amount)}</span>
                              {Number(e.chargeback_amount) > 0 ? <span className="sl-muted" style={{ display: 'block' }}>− {money(e.chargeback_amount)} chargeback</span> : null}
                            </TCell>
                            <TCell>{shortDate(e.created_at)}</TCell>
                            <TCell>
                              <span className="sl-actions">
                                {(ACTIONS[e.status] || []).map(([target, label, variant]) => (
                                  <Button key={target} size="sm" variant={variant} loading={busy === e.id} onClick={() => doTransition(e, target, label)}>
                                    {label}
                                  </Button>
                                ))}
                              </span>
                            </TCell>
                          </TRow>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                ) : null}
              </div>
            );
          })}
          {!groups.length ? <p className="rv-loading">No entries match.</p> : null}
        </div>
      )}
    </div>
  );
}

// ── Profiles Tab ────────────────────────────────────────────────
const EMPTY_PROFILE = {
  name: '', trigger: '', structure: '',
  rate: '', flat_amount: '', min_contract_value: '0',
  chargeback_policy: '', chargeback_days: '90',
  commission_window_start: '',
  tier_mode: 'bracket',
  tiers: [],  // optional — empty = flat base rate for every completion
  bonus_threshold: '', bonus_amount: '',
};

function ProfilesTab() {
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ ...EMPTY_PROFILE });
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    apiClient.get('/analytics/commissions/profiles/').then(r => setProfiles(r.data || []))
      .catch(() => toast.error('Failed to load profiles'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setForm({ ...EMPTY_PROFILE }); setEditId(null); setShowForm(true); };
  const openEdit = (p) => {
    setForm({
      name: p.name, trigger: p.trigger, structure: p.structure,
      rate: String(p.rate), flat_amount: p.flat_amount != null ? String(p.flat_amount) : '',
      min_contract_value: String(p.min_contract_value || 0),
      chargeback_policy: p.chargeback_policy, chargeback_days: String(p.chargeback_days),
      commission_window_start: p.commission_window_start || '',
      tier_mode: p.tier_mode || 'bracket',
      tiers: (p.tiers || []).map(t => ({
        threshold_service_value: String(t.threshold_service_value), rate: String(t.rate),
      })),
      bonus_threshold: p.flat_bonus?.threshold_service_value ? String(p.flat_bonus.threshold_service_value) : '',
      bonus_amount: p.flat_bonus?.amount ? String(p.flat_bonus.amount) : '',
    });
    setEditId(p.id);
    setShowForm(true);
  };

  const save = async () => {
    if (!form.name || !form.trigger || !form.structure) {
      toast.error('Name, trigger, and structure are required');
      return;
    }
    if (form.structure !== 'flat' && !form.rate) {
      toast.error('Rate is required for percentage-based structures');
      return;
    }
    setSaving(true);
    const payload = { ...form, rate: form.rate, chargeback_days: Number(form.chargeback_days) };
    if (!payload.flat_amount) delete payload.flat_amount;
    payload.commission_window_start = form.commission_window_start || null;
    payload.tiers = (form.tiers || [])
      .filter(t => t.threshold_service_value !== '' && t.rate !== '')
      .map(t => ({ threshold_service_value: t.threshold_service_value, rate: t.rate }));
    payload.flat_bonus = (form.bonus_threshold && form.bonus_amount)
      ? { threshold_service_value: form.bonus_threshold, amount: form.bonus_amount }
      : null;
    delete payload.bonus_threshold; delete payload.bonus_amount;
    try {
      if (editId) await apiClient.put(`/analytics/commissions/profiles/${editId}/`, payload);
      else await apiClient.post('/analytics/commissions/profiles/', payload);
      toast.success(editId ? 'Profile updated' : 'Profile created');
      setShowForm(false);
      load();
    } catch (e) { toast.error(e?.message || 'Save failed'); }
    finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!window.confirm('Delete this profile?')) return;
    try {
      await apiClient.delete(`/analytics/commissions/profiles/${id}/`);
      toast.success('Profile removed');
      load();
    } catch (e) { toast.error(e?.message || 'Delete failed'); }
  };

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const setTier = (i, k, v) => setForm(f => ({
    ...f, tiers: f.tiers.map((t, j) => (j === i ? { ...t, [k]: v } : t)),
  }));
  const addTier = () => setForm(f => ({
    ...f, tiers: [...(f.tiers || []), {
      threshold_service_value: (f.tiers || []).length === 0 ? '0' : '', rate: '',
    }],
  }));
  const removeTier = (i) => setForm(f => ({ ...f, tiers: f.tiers.filter((_, j) => j !== i) }));

  return (
    <div className="sl-section">
      <div className="sl-section-head">
        <h2>Commission profiles</h2>
        <div className="sl-end">{showForm ? null : <Button variant="secondary" size="sm" onClick={openCreate}>New profile</Button>}</div>
      </div>
      {showForm ? (
        <div className="sl-pane">
          <div className="sl-form" style={{ '--sl-cols': 3 }}>
            <label className="sl-field">Name<input value={form.name} onChange={e => set('name', e.target.value)} /></label>
            <label className="sl-field">Trigger
              <select value={form.trigger} onChange={e => set('trigger', e.target.value)}>
                <option value="">Select a trigger</option>
                {Object.entries(TRIGGER_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </label>
            <label className="sl-field">Structure
              <select value={form.structure} onChange={e => set('structure', e.target.value)}>
                <option value="">Select a structure</option>
                {Object.entries(STRUCTURE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </label>
            <label className="sl-field">Rate (decimal)<input type="number" step="0.01" value={form.rate} onChange={e => set('rate', e.target.value)} /></label>
            <label className="sl-field">Flat amount ($)<input type="number" step="0.01" value={form.flat_amount} onChange={e => set('flat_amount', e.target.value)} placeholder="For a flat structure" /></label>
            <label className="sl-field">Min contract value<input type="number" step="1" value={form.min_contract_value} onChange={e => set('min_contract_value', e.target.value)} /></label>
            <label className="sl-field">Chargeback policy
              <select value={form.chargeback_policy} onChange={e => set('chargeback_policy', e.target.value)}>
                <option value="">Select a policy</option>
                {Object.entries(CHARGEBACK_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </label>
            <label className="sl-field">Chargeback window (days)<input type="number" value={form.chargeback_days} onChange={e => set('chargeback_days', e.target.value)} /></label>
            <label className="sl-field">Commission window start (FieldRoutes eligibility)
              <input type="date" value={form.commission_window_start} onChange={e => set('commission_window_start', e.target.value)} />
              <small>Plans sold before this date never accrue. Moving it recomputes shadow entries (paid ones stay locked).</small>
            </label>
          </div>

          {/* ── Tier ladder ── */}
          <div className="sl-section">
            <div className="sl-actions">
              <b>Tier ladder (monthly service value)</b>
              <label className="sl-controls">
                <span className="sl-muted">Mode</span>
                <select value={form.tier_mode} onChange={e => set('tier_mode', e.target.value)}>
                  <option value="bracket">Bracket (marginal)</option>
                  <option value="whole_month">Whole month (re-rates)</option>
                </select>
                <Hint text={form.tier_mode === 'whole_month' ? HINTS.tier_mode_whole : HINTS.tier_mode_bracket} />
              </label>
            </div>
            {!(form.tiers || []).length ? (
              <p className="rv-note">
                No tier ladder — the flat base rate{form.rate ? ` (${rate(form.rate)})` : ''} applies to every completion.
                Add tiers only if rates should change with monthly volume.
              </p>
            ) : null}
            {(form.tiers || []).map((t, i) => (
              <div key={i} className="sl-controls">
                <span className="sl-muted">Tier {i + 1}</span>
                <label className="sl-controls"><span className="sl-muted">From $</span>
                  <input type="number" step="1" style={{ width: 110 }} value={t.threshold_service_value} onChange={e => setTier(i, 'threshold_service_value', e.target.value)} disabled={i === 0} />
                  <Hint text={HINTS.threshold} />
                </label>
                <label className="sl-controls"><span className="sl-muted">Rate</span>
                  <input type="number" step="0.01" min="0" max="1" style={{ width: 90 }} value={t.rate} onChange={e => setTier(i, 'rate', e.target.value)} />
                  <span className="sl-muted">{t.rate ? rate(t.rate) : ''}</span>
                </label>
                <Button variant="ghost" size="sm" onClick={() => removeTier(i)}>Remove</Button>
              </div>
            ))}
            <div><Button variant="ghost" size="sm" onClick={addTier}>Add tier</Button></div>
            <div className="sl-controls">
              <b>Flat bonus</b>
              <Hint text={HINTS.bonus} />
              <label className="sl-controls"><span className="sl-muted">At $</span>
                <input type="number" step="1" placeholder="off" style={{ width: 110 }} value={form.bonus_threshold} onChange={e => set('bonus_threshold', e.target.value)} />
              </label>
              <label className="sl-controls"><span className="sl-muted">Add $</span>
                <input type="number" step="1" placeholder="off" style={{ width: 110 }} value={form.bonus_amount} onChange={e => set('bonus_amount', e.target.value)} />
              </label>
            </div>
          </div>
          <div className="sl-actions">
            <Button variant="primary" size="sm" onClick={save} loading={saving}>{editId ? 'Save profile' : 'Create profile'}</Button>
            <Button size="sm" variant="ghost" onClick={() => setShowForm(false)}>Cancel</Button>
          </div>
        </div>
      ) : null}
      {loading ? <Loading /> : (
        <DataTable
          columns={[
            { key: 'name', label: 'Name', render: (p) => <b>{p.name}</b> },
            { key: 'trigger', label: 'Trigger', render: (p) => TRIGGER_LABELS[p.trigger] || words(p.trigger) },
            { key: 'structure', label: 'Structure', render: (p) => STRUCTURE_LABELS[p.structure] || words(p.structure) },
            { key: 'rate', label: 'Rate', num: true, render: (p) => (p.structure === 'flat' ? money(p.flat_amount) : rate(p.rate)) },
            { key: 'chargeback_policy', label: 'Chargeback', render: (p) => `${CHARGEBACK_LABELS[p.chargeback_policy] || words(p.chargeback_policy)}${p.chargeback_policy !== 'none' ? ` (${p.chargeback_days}d)` : ''}` },
            { key: 'is_active', label: 'Active', render: (p) => <Badge tone={p.is_active ? 'success' : 'neutral'}>{p.is_active ? 'Active' : 'Inactive'}</Badge> },
            {
              key: 'actions', label: '', sort: false, render: (p) => (
                <span className="sl-actions">
                  <Button size="sm" variant="ghost" onClick={() => openEdit(p)}>Edit</Button>
                  <Button size="sm" variant="danger" onClick={() => del(p.id)}>Delete</Button>
                </span>
              ),
            },
          ]}
          rows={profiles}
          empty="No profiles yet."
        />
      )}
    </div>
  );
}

// ── Batches Tab ─────────────────────────────────────────────────
function BatchesTab() {
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [periodStart, setPeriodStart] = useState('');
  const [periodEnd, setPeriodEnd] = useState('');
  const [creating, setCreating] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    apiClient.get('/analytics/commissions/batches/').then(r => setBatches(r.data || []))
      .catch(() => toast.error('Failed to load batches'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!periodStart || !periodEnd) { toast.error('Select both dates'); return; }
    setCreating(true);
    try {
      await apiClient.post('/analytics/commissions/batches/', { period_start: periodStart, period_end: periodEnd });
      toast.success('Batch created');
      setShowCreate(false);
      load();
    } catch (e) { toast.error(e?.message || 'Create failed'); }
    finally { setCreating(false); }
  };

  const transition = async (id, status) => {
    try {
      await apiClient.put(`/analytics/commissions/batches/${id}/`, { status });
      toast.success(`Batch ${status}`);
      load();
    } catch (e) { toast.error(e?.message || 'Transition failed'); }
  };

  return (
    <div className="sl-section">
      <div className="sl-section-head">
        <h2>Payout batches</h2>
        <div className="sl-end">{showCreate ? null : <Button variant="secondary" size="sm" onClick={() => setShowCreate(true)}>New batch</Button>}</div>
      </div>
      {showCreate ? (
        <div className="sl-pane">
          <div className="sl-controls">
            <label className="sl-field">Period start<input type="date" value={periodStart} onChange={e => setPeriodStart(e.target.value)} /></label>
            <label className="sl-field">Period end<input type="date" value={periodEnd} onChange={e => setPeriodEnd(e.target.value)} /></label>
          </div>
          <div className="sl-actions">
            <Button variant="primary" size="sm" onClick={create} loading={creating}>Create batch</Button>
            <Button size="sm" variant="ghost" onClick={() => setShowCreate(false)}>Cancel</Button>
          </div>
        </div>
      ) : null}
      {loading ? <Loading /> : (
        <DataTable
          columns={[
            { key: 'period_start', label: 'Period', render: (b) => `${shortDate(b.period_start)} – ${shortDate(b.period_end)}` },
            { key: 'status', label: 'Status', render: (b) => <StatusBadge status={b.status} /> },
            { key: 'total_gross', label: 'Gross', num: true, render: (b) => money(b.total_gross) },
            { key: 'total_chargebacks', label: 'Chargebacks', num: true, render: (b) => money(b.total_chargebacks) },
            { key: 'total_net', label: 'Net', num: true, render: (b) => <b>{money(b.total_net)}</b> },
            {
              key: 'actions', label: '', sort: false, render: (b) => (
                <>
                  {b.status === 'open' ? <Button size="sm" variant="secondary" onClick={() => transition(b.id, 'reviewed')}>Review</Button> : null}
                  {b.status === 'reviewed' ? <Button size="sm" variant="secondary" onClick={() => transition(b.id, 'paid')}>Mark paid</Button> : null}
                </>
              ),
            },
          ]}
          rows={batches}
          empty="No batches yet."
        />
      )}
    </div>
  );
}

// ── Accrual Tab (FR-direct shadow accrual + excluded/held buckets) ─
function AccrualTab() {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [openSellers, setOpenSellers] = useState({});

  const run = useCallback((opts = {}) => {
    setLoading(true);
    const { live = false, recompute = false } = opts;
    const req = live
      ? apiClient.post('/analytics/commissions/accrual/run/', { dry_run: false, recompute })
      : apiClient.get('/analytics/commissions/accrual/run/');
    req.then(r => setReport(r.data))
      .catch(e => toast.error(e?.response?.data?.detail || 'Accrual run failed'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { run(); }, [run]);

  const b = report?.buckets || {};
  const excludedKeys = Object.keys(b).filter(k => k.startsWith('excluded'));
  const att = b.attributed || {};
  const perSeller = report?.per_seller || {};
  const num = (v) => Number(v || 0);

  return (
    <div className="sl-section">
      <p className="sl-notice is-warn">
        Shadow mode: accrued entries are projected and not payable. Nothing here pays out until it is explicitly approved.
      </p>
      <div className="sl-actions">
        <Button size="sm" variant="secondary" loading={loading} onClick={() => run()}>Dry run (preview)</Button>
        <Button size="sm" variant="primary" loading={loading}
          onClick={() => { if (window.confirm('Write shadow PROJECTED/HELD entries for this tenant? Nothing becomes payable.')) run({ live: true }); }}>
          Run shadow accrual
        </Button>
        <Button size="sm" variant="ghost" loading={loading}
          onClick={() => { if (window.confirm('Recompute shadow entries after a window change? Paid/batched entries stay locked & flagged.')) run({ live: true, recompute: true }); }}>
          Recompute (window change)
        </Button>
        {report ? <span className="sl-muted">Window: {report.window_start || 'not set'} · {report.dry_run ? 'dry run' : 'written'}</span> : null}
      </div>

      {report ? (
        <>
          <StatStrip stats={[
            { label: 'Plans', value: count(report.plans_total), note: `${count(report.plans_before_window)} sold before the window` },
            { label: 'Attributed', value: `${count(att.plans)} plans`, note: `${count(att.services)} services → ${money(att.commission)}` },
            { label: 'Held for review', value: `${count(b.held_for_review?.plans)} plans`, note: 'recurring / unpriced · not valued' },
            { label: 'Excluded', value: `${count(excludedKeys.reduce((s, k) => s + num(b[k]?.plans), 0))} plans`, note: 'no comp profile / unmapped' },
          ]} />

          {report.shape_counts ? (
            <p className="rv-note">Billing shapes in window: {Object.entries(report.shape_counts).map(([k, v]) => `${words(k)} ${count(v)}`).join(' · ')}</p>
          ) : null}

          {/* One row per seller (attributed + excluded + held = total). Expand for
              the completions the backend accrued, each with its own value and
              commission as computed — the row's total is the backend's. */}
          <div>
            {Object.entries(perSeller)
              .sort((a, c) => {
                const ea = a[1].status === 'eligible' ? 0 : 1;   // excluded sinks below eligible
                const ec = c[1].status === 'eligible' ? 0 : 1;
                return ea - ec || num(c[1].accrued_commission) - num(a[1].accrued_commission) || c[1].plans - a[1].plans;
              })
              .map(([k, s]) => {
                const open = !!openSellers[k];
                const detail = s.plans_detail || [];
                return (
                  <div key={k} className="sl-group">
                    <div className="sl-group-row" role="button" tabIndex={0} aria-expanded={open}
                      onClick={() => setOpenSellers(o => ({ ...o, [k]: !o[k] }))}
                      onKeyDown={(ev) => { if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); setOpenSellers(o => ({ ...o, [k]: !o[k] })); } }}>
                      <span className="sl-caret" aria-hidden="true">{open ? '▾' : '▸'}</span>
                      <b>{s.label}</b>
                      <span className="sl-actions">
                        <Badge tone={s.status === 'eligible' ? 'success' : 'neutral'}>{words(s.status)}</Badge>
                        <Hint text={BUCKET_HINTS[s.status] || s.status} />
                      </span>
                      <span className="sl-muted">{s.plans} plan{s.plans === 1 ? '' : 's'}{s.held_plans ? ` · ${s.held_plans} held` : ''}</span>
                      <span className="sl-end">
                        <span className="sl-muted">Month to date {money(s.mtd_service_value)}</span>
                        <span className="sl-muted">
                          {s.mtd_tier_rate ? rate(s.mtd_tier_rate) : '—'}
                          {s.mtd_next_threshold ? ` · next at ${money(s.mtd_next_threshold)}` : ''}
                        </span>
                        <b>{money(s.accrued_commission)}</b>
                      </span>
                    </div>
                    {open ? (
                      <div className="sl-group-body">
                        <Table>
                          <THead columns={['Customer', 'Plan', 'Sold', 'Completions this month (value → commission)']} />
                          <tbody>
                            {detail.map(p => (
                              <TRow key={p.plan}>
                                <TCell>{p.customer || '—'}</TCell>
                                <TCell>FR-{p.plan}</TCell>
                                <TCell>{p.sold || '—'}</TCell>
                                <TCell>
                                  {p.held_reason ? (
                                    <span className="sl-actions">
                                      <Badge tone="warning">held: {p.held_reason}</Badge>
                                      <Hint text={BUCKET_HINTS.held} />
                                    </span>
                                  ) : (p.completions || []).length ? (
                                    (p.completions || []).map(c => (
                                      <span key={c.id} className="sl-num" style={{ display: 'block' }}>{c.date} · #{c.id} · {money(c.value)} → {money(c.commission)}</span>
                                    ))
                                  ) : (
                                    <span className="sl-muted">None yet</span>
                                  )}
                                </TCell>
                              </TRow>
                            ))}
                            {!detail.length ? <TEmpty colSpan={4}>No plan detail. Run the accrual to fill it.</TEmpty> : null}
                          </tbody>
                        </Table>
                      </div>
                    ) : null}
                  </div>
                );
              })}
            {!Object.keys(perSeller).length ? <p className="rv-loading">No plans in the window.</p> : null}
          </div>
          {report.recompute ? (
            <p className="rv-note">Recompute: {report.recompute.flagged_locked} locked entries flagged for review · {report.recompute.purged_unlocked} shadow entries regenerated.</p>
          ) : null}
        </>
      ) : null}
    </div>
  );
}

// ── Reps Tab (roster + profile assignment, admin) ───────────────
function RepsTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState({});   // ext_id -> {user_id} (unmapped rows only)
  const [saving, setSaving] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [hideInactive, setHideInactive] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    apiClient.get('/analytics/commissions/reps/')
      .then(r => setData(r.data))
      .catch(() => toast.error('Failed to load rep roster'))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  // Auto-save on change — no separate Save button to hunt for.
  const save = async (row, { userId, profileId }) => {
    setSaving(row.external_id);
    try {
      await apiClient.post('/analytics/commissions/reps/assign/', {
        user_id: Number(userId),
        profile_id: profileId ? Number(profileId) : null,
        ...(row.user ? {} : { external_sales_rep_id: row.external_id }),
      });
      toast.success(`Saved — ${row.fr_name || row.user?.email || row.external_id}`);
      setDraft(dr => { const c = { ...dr }; delete c[row.external_id]; return c; });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally { setSaving(null); }
  };

  const onProfileChange = (row, profileId) => {
    const userId = row.user?.id ?? draft[row.external_id]?.user_id;
    if (!userId) { toast.error('Map this seller to a CRM user first'); return; }
    save(row, { userId, profileId });
  };

  const onUserChange = (row, userId) => {
    if (!userId) return;
    setDraft(dr => ({ ...dr, [row.external_id]: { user_id: userId } }));
    // mapping alone is worth persisting immediately; profile can follow
    save(row, { userId, profileId: row.assignment?.profile_id ?? null });
  };

  const statusTone = { eligible: 'success', no_profile: 'neutral', unmapped: 'neutral' };
  const allRows = data?.rows || [];
  const rows = allRows.filter(r =>
    (!statusFilter || r.status === statusFilter)
    && (!hideInactive || r.plans_in_window > 0)
  );
  const profiles = data?.profiles || [];
  const users = data?.tenant_users || [];

  return (
    <div className="sl-section">
      <p className="rv-note">
        Every seller with sales in FieldRoutes — all time, not just the commission window
        (window starts {data?.window_start || '—'}). Changes save automatically; only assigned sellers accrue.
      </p>
      <div className="sl-controls">
        <div className="rv-chips" role="toolbar" aria-label="Filter sellers">
          {['', 'eligible', 'no_profile', 'unmapped'].map(s => (
            <button key={s || 'all'} type="button" className={`rv-chip${statusFilter === s ? ' is-active' : ''}`} aria-pressed={statusFilter === s} onClick={() => setStatusFilter(s)}>
              {s ? sentence(s) : 'All'}
            </button>
          ))}
        </div>
        <label className="sl-check">
          <input type="checkbox" checked={hideInactive} onChange={e => setHideInactive(e.target.checked)} />
          Hide sellers with no in-window sales
        </label>
        <span className="sl-muted" style={{ marginLeft: 'auto' }}>{rows.length} of {allRows.length} sellers</span>
      </div>

      {loading ? <Loading /> : (
        <Table>
          <THead columns={['Seller', 'FR ID', 'Status', { label: 'Plans (all time)', num: true }, { label: 'In window', num: true }, 'CRM user', 'Commission profile (saves on change)']} />
          <tbody>
            {rows.map(r => {
              const busyRow = saving === r.external_id;
              const draftUser = draft[r.external_id]?.user_id || '';
              return (
                <TRow key={r.external_id} style={busyRow ? { opacity: 0.6 } : undefined}>
                  <TCell>{r.fr_name || r.user?.name || r.user?.email || '—'}</TCell>
                  <TCell className="sl-muted">{r.external_id}</TCell>
                  <TCell>
                    <span className="sl-actions">
                      <Badge tone={statusTone[r.status] || 'neutral'}>{words(r.status)}</Badge>
                      <Hint text={BUCKET_HINTS[r.status === 'no_profile' ? 'excluded_no_profile' : r.status === 'unmapped' ? 'excluded_unmapped' : r.status] || r.status} />
                    </span>
                  </TCell>
                  <TCell num>{count(r.plans_total)}</TCell>
                  <TCell num>{count(r.plans_in_window)}</TCell>
                  <TCell>
                    {r.user ? r.user.email : (
                      <select className="sl-input" disabled={busyRow} value={draftUser} onChange={e => onUserChange(r, e.target.value)} aria-label="Map to a CRM user">
                        <option value="">Map to a user</option>
                        {users.map(u => <option key={u.id} value={u.id}>{u.email}</option>)}
                      </select>
                    )}
                  </TCell>
                  <TCell>
                    <select className="sl-input" disabled={busyRow} value={r.assignment?.profile_id ?? ''} onChange={e => onProfileChange(r, e.target.value)} aria-label="Commission profile">
                      <option value="">None (no accrual)</option>
                      {profiles.map(p => <option key={p.id} value={p.id}>{p.name}{p.is_active ? '' : ' (inactive)'}</option>)}
                    </select>
                  </TCell>
                </TRow>
              );
            })}
            {!rows.length ? <TEmpty colSpan={7}>No sellers match the filter.</TEmpty> : null}
          </tbody>
        </Table>
      )}
    </div>
  );
}

// ── My Commissions (rep-facing: sees ONLY their own) ────────────
function MyCommissionsView() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let m = true;
    apiClient.get('/analytics/commissions/my/')
      .then(r => { if (m) setData(r.data); })
      .catch(() => toast.error('Failed to load your commissions'))
      .finally(() => { if (m) setLoading(false); });
    return () => { m = false; };
  }, []);

  const s = data?.summary || {};
  return (
    <div className="rv-screen">
      <RevivalHeader title="My commissions" />
      {loading ? <Loading /> : (
        <>
          {/* The backend's own five totals, as sent. "Earned" already includes
              payable and paid — it is not added to anything here. */}
          <StatStrip stats={[
            { label: 'Projected', value: money(s.total_projected), note: 'shadow · not payable yet' },
            { label: 'Pending', value: money(s.total_pending) },
            { label: 'Earned', value: money(s.total_earned), note: 'includes payable and paid' },
            { label: 'Paid', value: money(s.total_paid) },
            { label: 'Chargebacks', value: money(s.total_chargebacks) },
          ]} />
          <DataTable
            columns={[
              { key: 'status', label: 'Status', render: (e) => <StatusBadge status={e.status} /> },
              { key: 'customer_name', label: 'Customer', render: (e) => e.customer_name || '—' },
              { key: 'contract_value', label: 'Service value', num: true, render: (e) => money(e.contract_value) },
              { key: 'commission_amount', label: 'Commission', num: true, render: (e) => <b>{money(e.commission_amount)}</b> },
              { key: 'created_at', label: 'Accrued', render: (e) => shortDate(e.created_at) },
            ]}
            rows={data?.entries || []}
            empty="No commission entries yet."
          />
        </>
      )}
    </div>
  );
}

// ── Projection Tab ──────────────────────────────────────────────
function ProjectionTab() {
  const today = new Date().toISOString().slice(0, 10);
  const [from, setFrom] = useState('2026-06-01');
  const [to, setTo] = useState(today);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(null); // `${rep_key}|${month}`
  const [detail, setDetail] = useState({});       // keyed by rep_key

  const toggleRow = (repKey, month) => {
    const key = `${repKey}|${month}`;
    if (expanded === key) { setExpanded(null); return; }
    setExpanded(key);
    if (!detail[repKey]) {
      apiClient.get(`/analytics/commissions/what-if/detail/?from=${from}&to=${to}&rep=${encodeURIComponent(repKey)}`)
        .then(r => setDetail(d => ({ ...d, [repKey]: r.data })))
        .catch(() => toast.error('Failed to load service detail'));
    }
  };

  const load = useCallback(() => {
    setLoading(true);
    apiClient.get(`/analytics/commissions/what-if/?from=${from}&to=${to}`)
      .then(r => setData(r.data))
      .catch(() => toast.error('Failed to load projection'))
      .finally(() => setLoading(false));
  }, [from, to]);

  useEffect(() => { load(); }, [load]);

  const h = data?.headline;
  return (
    <div className="sl-section">
      <p className="sl-notice is-warn">Projection — not payable. A dry run over synced FieldRoutes completions; it creates no commission entries.</p>
      <div className="sl-controls">
        <input type="date" value={from} onChange={e => setFrom(e.target.value)} aria-label="From" />
        <span className="sl-muted">to</span>
        <input type="date" value={to} onChange={e => setTo(e.target.value)} aria-label="To" />
      </div>
      {loading ? <div className="rv-loading">Loading projection…</div> : null}
      {data ? (
        <>
          {h ? (
            <StatStrip stats={[
              { label: 'Completed revenue (window)', value: money(h.total_gross) },
              { label: `Rep-attributed (${h.attributed?.pct ?? '—'}%)`, value: money(h.attributed?.gross), note: `${count(h.attributed?.services)} services → ${money(h.attributed?.commission)} projected at ${data.rate ?? '—'}` },
              { label: `Unattributed base (${h.unattributed_base?.pct ?? '—'}%)`, value: money(h.unattributed_base?.gross), note: `${count(h.unattributed_base?.services)} services · unmapped sellers · nothing payable` },
            ]} />
          ) : null}
          {h?.note ? <p className="rv-note">{h.note}</p> : null}
          {(data.warnings || []).map((w, i) => <p key={i} className="sl-notice is-bad">Data gap: {w}</p>)}
          <p className="rv-note">
            Coverage: {data.coverage.projected}/{data.coverage.completions_total} completions projected
            ({data.coverage.no_plan} without a plan, {data.coverage.plan_unpriced_or_uncapped} unpriced,
            {' '}{data.coverage.over_annual_cap} over the annual cap, {data.coverage.duplicates_skipped ?? 0} duplicates removed) · rate {data.rate} ·
            synced {data.data_freshness.latest_sync || 'unknown'}
          </p>
          <Table>
            <THead columns={['Rep', 'Month', { label: 'Services', num: true }, { label: 'Gross', num: true }, { label: 'Projected commission', num: true }]} />
            <tbody>
              {data.rows.map((r, i) => (
                <React.Fragment key={i}>
                  <TRow onClick={() => toggleRow(r.rep_key, r.month)} aria-expanded={expanded === `${r.rep_key}|${r.month}`}>
                    <TCell>
                      <span className="sl-caret" aria-hidden="true">{expanded === `${r.rep_key}|${r.month}` ? '▾' : '▸'}</span>{' '}
                      {r.rep}
                      {r.entity === 'system' ? <span className="sl-muted"> (system account, not a person)</span> : null}
                      {r.entity === 'unknown' ? <span className="sl-muted"> (FieldRoutes seller, no CRM user)</span> : null}
                    </TCell>
                    <TCell>{r.month}</TCell>
                    <TCell num>{count(r.services)}</TCell>
                    <TCell num>{money(r.gross)}</TCell>
                    <TCell num><b>{money(r.commission)}</b></TCell>
                  </TRow>
                  {expanded === `${r.rep_key}|${r.month}` ? (
                    <tr>
                      <td colSpan={5} className="sl-group-body">
                        {!detail[r.rep_key] ? <div className="rv-loading">Loading services…</div> : (
                          <Table>
                            <THead columns={['Date', 'Customer', 'Service', { label: 'Gross', num: true }, { label: 'Projected', num: true }]} />
                            <tbody>
                              {detail[r.rep_key].services.filter(s => s.month === r.month).map((s, j) => (
                                <TRow key={j}>
                                  <TCell>{s.date}</TCell>
                                  <TCell>
                                    <a className="pr-link" href={s.customer_id ? `/customers/${s.customer_id}` : `/customers/fr/${s.fr_customer_external_id}`}
                                      onClick={e => e.stopPropagation()}>
                                      {s.customer_name}
                                    </a>
                                  </TCell>
                                  <TCell>{s.service}</TCell>
                                  <TCell num>{money(s.gross)}</TCell>
                                  <TCell num>{money(s.commission)}</TCell>
                                </TRow>
                              ))}
                            </tbody>
                          </Table>
                        )}
                      </td>
                    </tr>
                  ) : null}
                </React.Fragment>
              ))}
              {!data.rows.length ? <TEmpty colSpan={5}>No projectable completions in the window.</TEmpty> : null}
            </tbody>
          </Table>
        </>
      ) : null}
    </div>
  );
}

// Admin-level roles — mirrors backend COMMISSION_ADMIN_ROLES. Everyone else
// gets ONLY their own commissions (backend 403s enforce this regardless).
const ADMIN_LEVEL_ROLES = ['admin', 'owner', 'manager', 'ceo'];

const TABS = [
  { id: 'dashboard', label: 'Dashboard' },
  { id: 'entries', label: 'Entries' },
  { id: 'accrual', label: 'Accrual' },
  { id: 'reps', label: 'Reps' },
  { id: 'profiles', label: 'Profiles' },
  { id: 'batches', label: 'Batches' },
  { id: 'projection', label: 'Projection' },
];

export default function CommissionAdmin() {
  const [tab, setTab] = useState('dashboard');
  const role = String(getUserRole() || '').trim().toLowerCase();
  const isAdminLevel = ADMIN_LEVEL_ROLES.includes(role);

  if (!isAdminLevel) return <MyCommissionsView />;

  return (
    <div className="rv-screen">
      <RevivalHeader title="Commissions" />
      <TabBar tabs={TABS} value={tab} onChange={setTab} />
      {tab === 'dashboard' ? <DashboardTab /> : null}
      {tab === 'entries' ? <EntriesTab /> : null}
      {tab === 'accrual' ? <AccrualTab /> : null}
      {tab === 'reps' ? <RepsTab /> : null}
      {tab === 'profiles' ? <ProfilesTab /> : null}
      {tab === 'batches' ? <BatchesTab /> : null}
      {tab === 'projection' ? <ProjectionTab /> : null}
    </div>
  );
}
